import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, CircularProgress } from '@mui/material';
import { useEffect } from 'react';
import { fetchOnboarding, updateStep } from '../api/client.js';
import { WizardLayout } from '../components/WizardLayout.js';
import { WizardRouter } from '../wizard/WizardRouter.js';
import type { OnboardingPublic } from '@scriptpad/shared';

const visualStepFor = (data: OnboardingPublic): number => {
  switch (data.currentStep) {
    case 1:
      return 1;
    case 2:
      return 2;
    case 3:
    case 4:
      return 3;
    case 5:
      return 4;
    case 6:
      return 5;
    case 7:
      return 6;
    default:
      return 1;
  }
};

export const OnboardingPage = () => {
  const { guid } = useParams<{ guid: string }>();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['onboarding', guid],
    queryFn: () => fetchOnboarding(guid!),
    enabled: Boolean(guid),
  });

  const stepMutation = useMutation({
    mutationFn: (step: number) => updateStep(guid!, step),
    onSuccess: (data) => queryClient.setQueryData(['onboarding', guid], data),
  });

  useEffect(() => {
    if (query.data && query.data.status === 'pending') {
      // first visit - server will flip to in_progress on next mutation
    }
  }, [query.data]);

  if (!guid) return null;

  if (query.isLoading) {
    return (
      <WizardLayout visualStep={1}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      </WizardLayout>
    );
  }

  if (query.isError || !query.data) {
    return (
      <WizardLayout visualStep={1}>
        <Alert severity="error" sx={{ mt: 4 }}>
          We couldn't load your onboarding session. Your link may have expired or be invalid. Please contact
          ScriptPad if this keeps happening.
        </Alert>
      </WizardLayout>
    );
  }

  return (
    <WizardLayout visualStep={visualStepFor(query.data)}>
      <WizardRouter
        guid={guid}
        data={query.data}
        onAdvance={(nextStep) => stepMutation.mutate(nextStep)}
        onPatched={(data) => queryClient.setQueryData(['onboarding', guid], data)}
      />
    </WizardLayout>
  );
};
